import React, { useState, useEffect, useRef } from 'react';
import {
  Upload,
  Search,
  Grid3X3,
  List,
  Download,
  Eye,
  Trash2,
  Filter,
  Folder,
  Image,
  Video,
  FolderPlus,
  Play,
  RefreshCw,
  Plus,
  FileText,
  Box,
  Smartphone,
  Share2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useClient } from '../../contexts/ClientContext';
import mediaService from '../../services/mediaService';
import { MediaFile } from '../../services/mediaService';
import { Button } from '../ui/button';
import { useToast } from '@/hooks/use-toast';
import { ThreeDViewer } from './ThreeDViewer';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ModernFileUpload from './ModernFileUpload';
import { getApiUrl } from '../../utils/config';
import AuthenticatedImage from './AuthenticatedImage';
import contentExportService from '../../services/contentExportService';
import QRCodeGenerator from '../ar/QRCode';
import MediaDeleteConfirmDialog from './MediaDeleteConfirmDialog';
import { useQueryClient } from '@tanstack/react-query';
import { div } from 'framer-motion/client';


interface MediaLibraryProps {
  onMediaSelect?: (files: MediaFile[]) => void;
  selectionMode?: boolean;
  className?: string;
}

const MediaLibrary: React.FC<MediaLibraryProps> = ({
  onMediaSelect,
  selectionMode = false,
  className = ''
}) => {
  const { currentClient, canDelete, canCreate } = useClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [allMediaFiles, setAllMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'image' | 'video' | 'model' | 'other'>('all');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [showUpload, setShowUpload] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState<MediaFile[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [show3DViewer, setShow3DViewer] = useState(false);
  const [selected3DModel, setSelected3DModel] = useState<MediaFile | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Keyboard navigation for image viewer
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!selectedMedia) return;
      
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const currentIndex = mediaFiles.findIndex(f => f.id === selectedMedia.id);
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : mediaFiles.length - 1;
        setSelectedMedia(mediaFiles[prevIndex]);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        const currentIndex = mediaFiles.findIndex(f => f.id === selectedMedia.id);
        const nextIndex = currentIndex < mediaFiles.length - 1 ? currentIndex + 1 : 0;
        setSelectedMedia(mediaFiles[nextIndex]);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setSelectedMedia(null);
      }
    };

    if (selectedMedia) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedMedia, mediaFiles]);

  // Reset zoom when changing images - start at 65%
  useEffect(() => {
    setImageZoom(0.60);
    setImagePosition({ x: 0, y: 0 });
  }, [selectedMedia]);

  // Constrain image position to prevent going out of bounds
  const constrainPosition = (zoom: number, position: { x: number, y: number }) => {
    if (zoom <= 0.65) {
      // When zoomed out to initial level or less, snap to center
      return { x: 0, y: 0 };
    }
    
    const container = imageContainerRef.current;
    if (!container) return position;
    
    const containerRect = container.getBoundingClientRect();
    
    // Simple approach: at higher zoom levels, allow more panning
    // The higher the zoom, the more we can pan
    const maxOffsetX = containerRect.width * (zoom - 0.5);
    const maxOffsetY = containerRect.height * (zoom - 0.5);
    
    return {
      x: Math.max(-maxOffsetX, Math.min(maxOffsetX, position.x)),
      y: Math.max(-maxOffsetY, Math.min(maxOffsetY, position.y))
    };
  };

  // Zoom functions
  const handleZoomIn = () => {
    const newZoom = Math.min(imageZoom * 1.5, 3.5); // Max 350%
    setImageZoom(newZoom);
    setImagePosition(prev => constrainPosition(newZoom, prev));
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(imageZoom / 1.5, 0.5); // Min 50%
    setImageZoom(newZoom);
    setImagePosition(prev => constrainPosition(newZoom, prev));
  };

  const handleZoomReset = () => {
    setImageZoom(0.60);
    setImagePosition({ x: 0, y: 0 });
  };

  // Mouse wheel zoom with center-based zooming
  const handleWheel = (event: React.WheelEvent) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      
      const delta = event.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.min(Math.max(imageZoom * delta, 0.5), 3.5); // Min 50%, Max 350%
      
      if (newZoom !== imageZoom) {
        // Always keep centered when zooming
        const newPosition = constrainPosition(newZoom, imagePosition);
        setImagePosition(newPosition);
        setImageZoom(newZoom);
      }
    }
  };

  // Mouse drag for panning when zoomed
  const handleMouseDown = (event: React.MouseEvent) => {
    if (imageZoom > 0.60) {
      event.preventDefault();
      setIsDragging(true);
      setDragStart({ 
        x: event.clientX - imagePosition.x, 
        y: event.clientY - imagePosition.y 
      });
    }
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (isDragging && imageZoom > 0.65) {
      event.preventDefault();
      const newPosition = {
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y
      };
      setImagePosition(constrainPosition(imageZoom, newPosition));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const loadMediaFiles = async (page = 1, forceRefresh = false) => {
    if (!currentClient) return;

    setLoading(true);
    setError(null);

    try {
      // For 3D models, we need to get all files and filter client-side
      // For other types, we can use server-side filtering
      const mediaType = selectedFilter === 'all' || selectedFilter === 'model' ? undefined : selectedFilter;

      const response = await mediaService.getMediaFiles(
        page,
        30,
        searchTerm || undefined,
        currentClient.id,
        mediaType
      );

      // Store all files for reference
      const allFiles = response.files || [];

      // Apply client-side filtering based on selected filter
      const filteredFiles = allFiles.filter(file => {
        // Always exclude brand-kit assets
        if (file.tags?.includes('brand-kit-asset')) return false;

        // Handle 3D model thumbnail filtering
        const is3DThumbnail = file.tags?.includes('3d-thumbnail') || file.tags?.includes('internal-thumbnail');
        const is3DModel = file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb');

        // For thumbnail files, check if there's a corresponding GLB file
        if (is3DThumbnail) {
          const taskIdMatch = file.original_filename.match(/meshy_3d_thumbnail_(.+)\.png$/);
          if (taskIdMatch) {
            const taskId = taskIdMatch[1];
            const hasCorrespondingGlb = allFiles.some(otherFile =>
              (otherFile.mime_type === 'model/gltf-binary' || otherFile.original_filename.endsWith('.glb')) &&
              otherFile.original_filename.includes(taskId)
            );

            // Hide thumbnail if GLB exists
            if (hasCorrespondingGlb) {
              console.log('🚫 Filtering out thumbnail file (has GLB):', file.original_filename);
              return false;
            }
          }
        }

        // Apply filter-specific logic
        switch (selectedFilter) {
          case 'all':
            return true;
          case 'image':
            // For images: exclude 3D model thumbnails, only show real images
            return file.mime_type.startsWith('image') && !is3DThumbnail;
          case 'video':
            return file.mime_type.startsWith('video');
          case 'model':
            // For 3D models: only show GLB files (thumbnails are already filtered out above)
            return is3DModel;
          case 'other':
            // For other: exclude images, videos, and 3D models
            return !file.mime_type.startsWith('image') &&
              !file.mime_type.startsWith('video') &&
              !is3DModel &&
              !is3DThumbnail;
          default:
            return true;
        }
      });

      setAllMediaFiles(allFiles);
      setMediaFiles(filteredFiles);
      setHasMore(response.has_next);
      setCurrentPage(page);

      console.log(`Loaded page ${page} with ${response.files?.length || 0} media files${forceRefresh ? ' (forced refresh)' : ''}`);

      // Debug: Log file type breakdown
      const fileTypes = response.files?.reduce((acc, file) => {
        const type = file.mime_type.split('/')[0] || 'other';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};
      console.log('📊 File types loaded:', fileTypes);

      // Debug: Log 3D model files and thumbnails
      const modelFiles = response.files?.filter(file =>
        file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb')
      ) || [];
      const thumbnailFiles = response.files?.filter(file =>
        file.tags?.includes('3d-thumbnail') || file.tags?.includes('internal-thumbnail')
      ) || [];

      if (modelFiles.length > 0 || thumbnailFiles.length > 0) {
        console.log(`🎯 Found ${modelFiles.length} 3D model files and ${thumbnailFiles.length} thumbnail files`);
        console.log('📁 GLB files:', modelFiles.map(f => ({
          id: f.id,
          filename: f.original_filename,
          tags: f.tags,
          has_thumbnail: !!f.thumbnail_url,
          thumbnail_url: f.thumbnail_url
        })));
        console.log('🖼️ Thumbnail files:', thumbnailFiles.map(f => ({
          id: f.id,
          filename: f.original_filename,
          tags: f.tags
        })));
      }
      window.scrollTo(0, 0);
    } catch (err) {
      setError('Failed to load media files');
      console.error('Error loading media files:', err);
    } finally {
      setLoading(false);
    }
  };





  useEffect(() => {
    loadMediaFiles(1, true);
  }, [currentClient]);

  // Reload when filter changes immediately
  useEffect(() => {
    if (currentClient) {
      loadMediaFiles(1, true);
    }
  }, [selectedFilter]);

  // Debounced search - reload after user stops typing
  useEffect(() => {
    if (currentClient) {
      const timeoutId = setTimeout(() => {
        loadMediaFiles(1, true);
      }, 500); // 500ms delay

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  const handleFileSelect = (fileId: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);

    if (onMediaSelect) {
      const selectedMediaFiles = mediaFiles.filter(file => newSelected.has(file.id));
      onMediaSelect(selectedMediaFiles);
    }
  };

  const handleUploadComplete = () => {
    setShowUpload(false);
    // Refresh immediately - no delay needed since upload is complete
    loadMediaFiles(1, true);
    toast({
      title: "Files uploaded successfully!"
    });
  };

  const handleDeleteFiles = async (files: MediaFile[], forceDelete: boolean = false) => {
    if (!currentClient) return;

    setIsDeleting(true);
    try {
      // Delete files with force parameter if needed
      await Promise.all(files.map(file => {
        const url = `${getApiUrl()}/api/v1/media/${file.id}${forceDelete ? '?force=true' : ''}`;
        return fetch(url, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'X-Client-ID': currentClient.id,
          },
        });
      }));

      // Remove deleted files from state
      setMediaFiles(prev => prev.filter(file => !files.some(deletedFile => deletedFile.id === file.id)));
      setSelectedFiles(new Set());

      const deletedCount = files.length;
      const message = forceDelete
        ? `${deletedCount} file${deletedCount > 1 ? 's' : ''} and associated posts deleted.`
        : `${deletedCount} file${deletedCount > 1 ? 's' : ''} deleted.`;

      toast({
        title: "Files deleted successfully",
        description: message
      });

      // Invalidate post groups query to refresh posts page if media files were used in posts
      if (forceDelete) {
        queryClient.invalidateQueries({ queryKey: ['post-groups'] });
        queryClient.invalidateQueries({ queryKey: ['postGroups'] }); // Alternative key format
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast({
        title: "Delete failed",
        description: "Some files could not be deleted. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setFilesToDelete([]);
    }
  };

  const handleBulkDelete = () => {
    const filesToDelete = mediaFiles.filter(file => selectedFiles.has(file.id));
    setFilesToDelete(filesToDelete);
    setShowDeleteConfirm(true);
  };

  const handleSingleDelete = (file: MediaFile) => {
    setFilesToDelete([file]);
    setShowDeleteConfirm(true);
  };

  const handleCreatePost = () => {
    if (selectedFiles.size === 0) {
      toast({
        title: "No Media Selected",
        description: "Please select at least one media file to create a post.",
        variant: "destructive",
      });
      return;
    }

    if (selectedFiles.size > 10) {
      toast({
        title: "Too Many Files Selected",
        description: "Please select 10 or fewer media files to create a post.",
        variant: "destructive",
      });
      return;
    }

    const selectedMediaIds = Array.from(selectedFiles);
    navigate('/app/posts/new', {
      state: {
        selectedMediaIds: selectedMediaIds,
        generatedFromMediaLibrary: true,
      }
    });
  };

  const selectAll = () => {
    // Only select files that are currently visible on this page
    const currentPageFileIds = new Set(filteredFiles.map(file => file.id));
    console.log(`📋 Select All: Selecting ${currentPageFileIds.size} files from current page ${currentPage}`);
    console.log(`📋 Files being selected:`, filteredFiles.map(f => f.original_filename));
    setSelectedFiles(currentPageFileIds);
    if (onMediaSelect) {
      onMediaSelect(filteredFiles);
    }
  };

  const deselectAll = () => {
    setSelectedFiles(new Set());
    if (onMediaSelect) {
      onMediaSelect([]);
    }
  };

  const downloadImage = async (file: MediaFile) => {
    if (!currentClient) return;

    try {
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'X-Client-ID': currentClient.id,
      };

      // Always use backend download endpoint to avoid CORS issues
      const downloadUrl = `${getApiUrl()}/api/v1/media/${file.id}/download`;

      const response = await fetch(downloadUrl, { headers });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.original_filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Track the export
        try {
          await contentExportService.trackExport({
            client_id: currentClient.id,
            media_file_id: file.id,
            content_type: 'media_download',
            export_format: file.mime_type.split('/')[1] || 'unknown',
            file_name: file.original_filename,
            file_size: file.file_size
          });
        } catch (exportError) {
          console.error('Failed to track export:', exportError);
          // Don't show error to user, just log it
        }

        toast({
          title: "Download completed",
          description: `${file.original_filename} has been downloaded.`
        });
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: "Download failed",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    }
  };

  const downloadMultipleFiles = async () => {
    if (!currentClient || selectedFiles.size === 0) return;

    const filesToDownload = mediaFiles.filter(file => selectedFiles.has(file.id));

    toast({
      title: "Starting downloads",
      description: `Downloading ${filesToDownload.length} file${filesToDownload.length > 1 ? 's' : ''}...`
    });

    let successCount = 0;
    let failCount = 0;

    // Download files sequentially to avoid overwhelming the browser
    for (const file of filesToDownload) {
      try {
        await downloadImage(file);
        successCount++;

        // Small delay between downloads to prevent browser issues
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Failed to download ${file.original_filename}:`, error);
        failCount++;
      }
    }

    // Show final result
    if (failCount === 0) {
      toast({
        title: "All downloads completed",
        description: `Successfully downloaded ${successCount} file${successCount > 1 ? 's' : ''}.`
      });
    } else {
      toast({
        title: "Downloads completed with errors",
        description: `${successCount} successful, ${failCount} failed.`,
        variant: failCount > successCount ? "destructive" : "default"
      });
    }
  };

  const downloadQRCode = () => {
    const svg = document.querySelector('#qr-code svg');
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const img = new window.Image();
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const pngFile = canvas.toDataURL('image/png');
          const downloadLink = document.createElement('a');
          downloadLink.download = 'qrcode.png';
          downloadLink.href = pngFile;
          downloadLink.click();
        };
        img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
      }
    }
  };

  const handleCreateAR = (file: MediaFile) => {
    if (!currentClient) return;

    // Check if it's a 3D model
    const is3DModel = file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb');

    if (!is3DModel) {
      toast({
        title: "Invalid File Type",
        description: "AR creation is only available for 3D models.",
        variant: "destructive"
      });
      return;
    }

    console.log('🎯 Creating AR for 3D model:', {
      filename: file.original_filename,
      mime_type: file.mime_type,
      id: file.id
    });

    // Find the source image for this 3D model
    // Look for a corresponding source image based on task ID or filename pattern
    let sourceImageId: string | undefined;
    let sourceImageUrl: string | undefined;

    // Try multiple patterns to extract task ID from the 3D model filename
    let taskId: string | undefined;

    // Pattern 1: meshy_3d_model_<taskId>.glb
    let taskIdMatch = file.original_filename.match(/meshy_3d_model_(.+)\.glb$/);
    if (taskIdMatch) {
      taskId = taskIdMatch[1];
      console.log('🎯 Found task ID (pattern 1):', taskId);
    }

    // Pattern 2: meshy_3d_<taskId>.glb (as used in AR Studio)
    if (!taskId) {
      taskIdMatch = file.original_filename.match(/meshy_3d_(.+)\.glb$/);
      if (taskIdMatch) {
        taskId = taskIdMatch[1];
        console.log('🎯 Found task ID (pattern 2):', taskId);
      }
    }

    // Pattern 3: Any filename containing a UUID-like pattern
    if (!taskId) {
      taskIdMatch = file.original_filename.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/);
      if (taskIdMatch) {
        taskId = taskIdMatch[1];
        console.log('🎯 Found task ID (UUID pattern):', taskId);
      }
    }

    if (taskId) {
      console.log('🔍 Searching for source image with task ID:', taskId);
      console.log('🔍 Available media files:', allMediaFiles.length);

      // Look for corresponding source image in all media files with multiple strategies
      let sourceImage = allMediaFiles.find(mediaFile => {
        const hasTaskId = mediaFile.original_filename.includes(taskId);
        const isImage = mediaFile.mime_type.startsWith('image');
        const notThumbnail = !mediaFile.tags?.includes('3d-thumbnail') && !mediaFile.tags?.includes('internal-thumbnail');

        console.log('🔍 Checking potential source image (strategy 1):', {
          filename: mediaFile.original_filename,
          mime_type: mediaFile.mime_type,
          tags: mediaFile.tags,
          hasTaskId,
          isImage,
          notThumbnail,
          matches: hasTaskId && isImage && notThumbnail
        });

        return hasTaskId && isImage && notThumbnail;
      });

      // Strategy 2: Look for images with the task ID but allow thumbnails
      if (!sourceImage) {
        console.log('🔍 Strategy 2: Looking for images with task ID (including thumbnails)');
        sourceImage = allMediaFiles.find(mediaFile => {
          const hasTaskId = mediaFile.original_filename.includes(taskId);
          const isImage = mediaFile.mime_type.startsWith('image');

          console.log('🔍 Checking potential source image (strategy 2):', {
            filename: mediaFile.original_filename,
            mime_type: mediaFile.mime_type,
            tags: mediaFile.tags,
            hasTaskId,
            isImage,
            matches: hasTaskId && isImage
          });

          return hasTaskId && isImage;
        });
      }

      // Strategy 3: Look for any image files that might be related (partial task ID match)
      if (!sourceImage) {
        console.log('🔍 Strategy 3: Looking for partial task ID matches');
        const shortTaskId = taskId.substring(0, 8); // First 8 characters
        sourceImage = allMediaFiles.find(mediaFile => {
          const hasPartialTaskId = mediaFile.original_filename.includes(shortTaskId);
          const isImage = mediaFile.mime_type.startsWith('image');

          console.log('🔍 Checking potential source image (strategy 3):', {
            filename: mediaFile.original_filename,
            mime_type: mediaFile.mime_type,
            shortTaskId,
            hasPartialTaskId,
            isImage,
            matches: hasPartialTaskId && isImage
          });

          return hasPartialTaskId && isImage;
        });
      }

      if (sourceImage) {
        sourceImageId = sourceImage.id;
        sourceImageUrl = sourceImage.file_url;
        console.log('✅ Found source image:', {
          id: sourceImageId,
          filename: sourceImage.original_filename
        });
      } else {
        console.log('❌ No source image found for task ID:', taskId);
      }
    } else {
      console.log('❌ Could not extract task ID from filename:', file.original_filename);
    }

    // Navigate to AR Studio with pre-selected 3D model and source image (if found)
    navigate('/app/content-hub/ar-studio', {
      state: {
        preSelectedModel: file.id,
        preSelectedSourceImage: sourceImageId,
        sourceImageUrl: sourceImageUrl
      }
    });

    toast({
      title: "Navigating to AR Studio",
      description: sourceImageId
        ? "Your 3D model and source image are pre-selected for AR ad generation."
        : "Your 3D model is pre-selected. You'll be prompted to select a source image in AR Studio.",
    });
  };



  // Server-side filtering is now handled in loadMediaFiles
  const filteredFiles = mediaFiles;

  if (!currentClient) {
    return (
      <div className="text-center py-12 px-4">
        <p className="text-slate-400 dark:text-slate-400">Please select a client to view media files.</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen text-foreground ${className}`}>
      {/* Header */}
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex-grow"
          >
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <Folder className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black bg-gradient-to-r from-gray-900 via-indigo-500 to-purple-500 bg-clip-text text-transparent dark:from-white dark:via-indigo-200 dark:to-purple-200">
                  Media Library
                </h1>
                <p className="hidden sm:block text-slate-400 dark:text-slate-400 text-base lg:text-lg">Your creative assets, organized beautifully</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="flex items-center space-x-2 sm:space-x-4"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="flex items-center bg-background/30 rounded-xl border border-border/30 p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-primary/10 text-primary' : 'text-slate-400 dark:text-slate-400'}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-primary/10 text-primary' : 'text-slate-400 dark:text-slate-400'}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                onClick={() => loadMediaFiles(1, true)}
                variant="outline"
                size="sm"
                className="text-blue-600 dark:text-blue-400 border-blue-300/50 dark:border-blue-600/50 bg-blue-50/50 dark:bg-blue-900/20 hover:bg-blue-100/50 dark:hover:bg-blue-900/30 backdrop-blur-sm transition-all duration-200"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin text-blue-500' : ''}`} />
                <span className="hidden sm:inline ml-2">Refresh</span>
              </Button>
              {canCreate() && (
                <Button
                  onClick={() => setShowUpload(true)}
                  className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold text-sm sm:text-base"
                >
                  <Upload className="h-5 w-5 sm:mr-2" />
                  <span className="hidden sm:inline">Upload Files</span>
                </Button>
              )}
            </div>
          </motion.div>
        </div>



        {/* Search Bar */}
        <motion.div
          className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="flex-1 w-full relative">
            <Search className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 dark:text-slate-400" />
            <input
              type="text"
              placeholder="Search your media files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 sm:pl-14 pr-6 py-3 sm:py-4 bg-white/70 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-300/50 dark:border-slate-600/30 rounded-2xl text-gray-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all duration-200"
            />
          </div>

          {/* Filter Controls */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <Select value={selectedFilter} onValueChange={(value: 'all' | 'image' | 'video' | 'model' | 'other') => setSelectedFilter(value)}>
                <SelectTrigger className="w-48 bg-white/70 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-300/50 dark:border-slate-600/30">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center space-x-2">
                      <Folder className="h-4 w-4" />
                      <span>All Files</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="image">
                    <div className="flex items-center space-x-2">
                      <Image className="h-4 w-4" />
                      <span>Images</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="video">
                    <div className="flex items-center space-x-2">
                      <Video className="h-4 w-4" />
                      <span>Videos</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="model">
                    <div className="flex items-center space-x-2">
                      <Box className="h-4 w-4" />
                      <span>3D Models</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="other">
                    <div className="flex items-center space-x-2">
                      <Folder className="h-4 w-4" />
                      <span>Other Files</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Selection Controls */}
          <div className="flex items-center space-x-3">
            {!selectionMode && (
              <div className="flex items-center space-x-2 bg-white/70 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-300/50 dark:border-slate-600/30 rounded-xl px-3 py-2">
                <Button
                  onClick={selectAll}
                  size="sm"
                  variant="ghost"
                  className="text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                >
                  Select All on Page
                </Button>
                {selectedFiles.size > 0 && (
                  <>
                    <div className="w-px h-4 bg-slate-300 dark:bg-slate-600"></div>
                    <Button
                      onClick={deselectAll}
                      size="sm"
                      variant="ghost"
                      className="text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                    >
                      Deselect All
                    </Button>
                  </>
                )}
              </div>
            )}

            {selectedFiles.size > 0 && (
              <div className="flex items-center space-x-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 px-4 py-2 rounded-xl border border-blue-200/50 dark:border-blue-600/30">
                <span className="text-blue-700 dark:text-blue-300 font-medium">
                  {selectedFiles.size} selected
                </span>


                {selectionMode ? (
                  <Button
                    onClick={() => {
                      const selectedMediaFiles = mediaFiles.filter(file => selectedFiles.has(file.id));
                      if (onMediaSelect) onMediaSelect(selectedMediaFiles);
                    }}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Use Selected
                  </Button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={downloadMultipleFiles}
                      size="sm"
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download ({selectedFiles.size})
                    </Button>
                    {/* Temporarily commented out Create Post button */}
                    {/* {selectedFiles.size <= 10 && (
                      <Button
                        onClick={handleCreatePost}
                        size="sm"
                        className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Create Post
                      </Button>
                    )} */}
                    {canDelete() && (
                      <Button
                        onClick={handleBulkDelete}
                        size="sm"
                        variant="destructive"
                        className="bg-red-500 hover:bg-red-600 text-white"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete ({selectedFiles.size})
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Content Area */}
      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-32">
              <div className="w-16 h-16 sm:w-20 sm:h-20 border-4 border-border/30 border-t-primary rounded-full animate-spin mb-8"></div>
              <h3 className="text-xl sm:text-2xl font-semibold text-foreground">Loading Your Media</h3>
              <p className="text-slate-400 dark:text-slate-400 text-base sm:text-lg">Organizing your creative assets...</p>
            </div>
          ) : error ? (
            <div className="text-center py-32 px-4">
              <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto bg-red-500/10 rounded-full flex items-center justify-center mb-8">
                <Image className="h-10 w-10 sm:h-12 sm:w-12 text-red-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-red-400 mb-3">Something went wrong</h3>
              <p className="text-slate-400 dark:text-slate-400 mb-8 text-base sm:text-lg">{error}</p>
              <Button
                onClick={() => loadMediaFiles(1, true)}
                className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-6 sm:px-8 py-3 rounded-xl"
              >
                Try Again
              </Button>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                {mediaFiles.length === 0 ? (
                  <FolderPlus className="h-8 w-8 sm:h-10 sm:w-10 text-indigo-400" />
                ) : (
                  <Filter className="h-8 w-8 sm:h-10 sm:w-10 text-indigo-400" />
                )}
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
                {mediaFiles.length === 0 ? 'No media files' : 'No files match your filters'}
              </h3>
              <p className="text-slate-400 dark:text-slate-400 text-base sm:text-lg mb-8">
                {mediaFiles.length === 0
                  ? 'Upload your first file to get started.'
                  : searchTerm
                    ? `No ${selectedFilter === 'all' ? '' : selectedFilter === 'model' ? '3D model' : selectedFilter === 'other' ? 'other' : selectedFilter} files found matching "${searchTerm}".`
                    : `No ${selectedFilter === 'all' ? '' : selectedFilter === 'model' ? '3D model' : selectedFilter === 'other' ? 'other' : selectedFilter} files found. Try a different filter.`
                }
              </p>
              {mediaFiles.length === 0 && canCreate() && (
                <Button
                  onClick={() => setShowUpload(true)}
                  className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-semibold"
                >
                  <FolderPlus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Upload Files
                </Button>
              )}
              {mediaFiles.length > 0 && (
                <Button
                  onClick={() => {
                    setSelectedFilter('all');
                    setSearchTerm('');
                  }}
                  variant="outline"
                  className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <div>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-6">
                  {filteredFiles.map((file) => (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className="relative bg-white dark:bg-slate-800/40 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 dark:border-slate-600/30 flex flex-col transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1 hover:border-indigo-500/50 dark:hover:shadow-indigo-500/10 dark:hover:border-indigo-500/50 cursor-pointer group"
                      onClick={() => {
                        if (selectionMode) {
                          handleFileSelect(file.id);
                        } else if (file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb')) {
                          setSelected3DModel(file);
                          setShow3DViewer(true);
                        } else {
                          setSelectedMedia(file);
                        }
                      }}
                    >
                      <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-t-2xl border-b border-slate-200 dark:border-slate-600/30">
                        {file.mime_type.startsWith('image') ? (
                          <img
                            src={file.thumbnail_url || file.file_url}
                            alt={file.original_filename}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              // Fallback to AuthenticatedMedia if direct image fails
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                const fallback = document.createElement('div');
                                fallback.className = 'w-full h-full bg-muted/50 flex items-center justify-center rounded-t-2xl';
                                fallback.innerHTML = '<div class="text-center"><svg class="h-8 w-8 text-slate-400 dark:text-slate-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg><p class="text-xs text-slate-400 dark:text-slate-400">Image unavailable</p></div>';
                                parent.appendChild(fallback);
                              }
                            }}
                          />
                        ) : file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb') ? (
                          <div className="relative w-full h-full">
                            {(() => {
                              // For 3D models, always use the thumbnail endpoint which now handles the redirect properly
                              const thumbnailUrl = `/api/v1/media/${file.id}/thumbnail`;

                              return (
                                <>
                                  <AuthenticatedImage
                                    src={thumbnailUrl}
                                    alt={file.original_filename}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    clientId={currentClient?.id}
                                    onError={() => {
                                      console.log('🔍 3D Model thumbnail failed to load:', {
                                        filename: file.original_filename,
                                        thumbnail_url: file.thumbnail_url,
                                        file_url: file.file_url,
                                        id: file.id
                                      });
                                    }}
                                  />
                                  {/* Fallback overlay for 3D models without thumbnails */}
                                  <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-md flex items-center justify-center mb-2">
                                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 2L3 7v6l7 5 7-5V7l-7-5zM8.5 8.5L10 7l1.5 1.5L10 10 8.5 8.5z" />
                                      </svg>
                                    </div>
                                    <p className="text-xs font-medium text-purple-700 dark:text-purple-300">3D Model</p>
                                  </div>
                                </>
                              );
                            })()}
                            {/* 3D Model indicator overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-purple-900/60 via-transparent to-transparent"></div>
                            <div className="absolute bottom-2 left-2 bg-purple-600/90 text-white px-2 py-1 rounded-md text-xs font-semibold backdrop-blur-sm">
                              3D Model
                            </div>
                            <div className="absolute top-2 right-2 bg-purple-600/90 text-white p-1 rounded-full backdrop-blur-sm">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2L3 7v6l7 5 7-5V7l-7-5zM8.5 8.5L10 7l1.5 1.5L10 10 8.5 8.5z" />
                              </svg>
                            </div>
                          </div>
                        ) : (
                          <AuthenticatedImage
                            src={file.thumbnail_url || file.file_url}
                            alt={file.original_filename}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            clientId={currentClient?.id}
                            onError={() => {
                              console.log('🔍 AuthenticatedImage failed to load:', {
                                filename: file.original_filename,
                                thumbnail_url: file.thumbnail_url,
                                file_url: file.file_url,
                                mime_type: file.mime_type,
                                media_type: file.media_type
                              });
                            }}
                          />
                        )}

                        {file.mime_type.startsWith('video') && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-black/50 rounded-full flex items-center justify-center">
                              <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white ml-1" />
                            </div>
                          </div>
                        )}

                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 rounded-2xl">
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb')) {
                                  setSelected3DModel(file);
                                  setShow3DViewer(true);
                                } else {
                                  setSelectedMedia(file);
                                }
                              }}
                              className="bg-white/20 hover:bg-white/30 text-white border-none backdrop-blur-sm"
                              title="Preview"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>

                            {/* AR Creation Button - Only show for 3D models */}
                            {(file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb')) && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('🎯 AR Button clicked for:', file.original_filename);
                                  handleCreateAR(file);
                                }}
                                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-none backdrop-blur-sm shadow-lg"
                                title="Create AR"
                              >
                                <Smartphone className="h-4 w-4" />
                              </Button>
                            )}

                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadImage(file);
                              }}
                              className="bg-white/20 hover:bg-white/30 text-white border-none backdrop-blur-sm"
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {canDelete() && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSingleDelete(file);
                                }}
                                className="bg-red-500/80 hover:bg-red-600/90 text-white border-none backdrop-blur-sm"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Selection Checkbox - Show on hover or when selected */}
                        {!selectionMode && (
                          <div className={`absolute top-2 right-2 sm:top-3 sm:right-3 transition-opacity duration-200 ${selectedFiles.has(file.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                            }`}>
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={selectedFiles.has(file.id)}
                                onChange={() => handleFileSelect(file.id)}
                                className="w-5 h-5 rounded-md border-2 border-white/80 bg-white/20 backdrop-blur-sm appearance-none cursor-pointer focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-0 transition-all duration-200"
                                onClick={(e) => e.stopPropagation()}
                              />
                              {selectedFiles.has(file.id) && (
                                <div className="absolute inset-0 w-5 h-5 bg-blue-600 rounded-md flex items-center justify-center pointer-events-none">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Selection Indicator */}
                        {selectedFiles.has(file.id) && (
                          <div className="absolute inset-0 bg-blue-500/20 border-2 border-blue-500 rounded-2xl pointer-events-none"></div>
                        )}
                      </div>

                      <div className="p-2 sm:p-3">
                        <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-white truncate mb-1">
                          {file.original_filename}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-400">
                          {mediaService.formatFileSize(file.file_size)}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredFiles.map((file) => (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                      className="relative flex items-center p-2 sm:p-3 bg-white dark:bg-slate-800/40 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 dark:border-slate-600/30 transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1 hover:border-indigo-500/50 dark:hover:shadow-indigo-500/10 dark:hover:border-indigo-500/50 cursor-pointer group"
                      onClick={() => {
                        if (selectionMode) {
                          handleFileSelect(file.id);
                        } else if (file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb')) {
                          setSelected3DModel(file);
                          setShow3DViewer(true);
                        } else {
                          setSelectedMedia(file);
                        }
                      }}
                    >
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden mr-4 flex-shrink-0 relative">
                        {file.mime_type.startsWith('image') ? (
                          <img
                            src={file.thumbnail_url || file.file_url}
                            alt={file.original_filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjMzc0MTUxIiByeD0iOCIvPgo8cGF0aCBkPSJNMjAgMjBIMjRWMjRIMjBWMjBaIiBmaWxsPSIjNjM3MzgwIi8+CjxwYXRoIGQ9Ik0xNiA0NEw0NCAzMkw0OCA0NEgxNloiIGZpbGw9IiM2MzczODAiLz4KPC9zdmc+';
                            }}
                          />
                        ) : file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb') ? (
                          <div className="relative w-full h-full">
                            <AuthenticatedImage
                              src={`/api/v1/media/${file.id}/thumbnail`}
                              alt={file.original_filename}
                              className="w-full h-full object-cover"
                              clientId={currentClient?.id}
                              onError={() => {
                                console.log('🔍 3D Model thumbnail failed to load (list view):', {
                                  filename: file.original_filename,
                                  thumbnail_url: file.thumbnail_url,
                                  file_url: file.file_url,
                                  id: file.id
                                });
                              }}
                            />
                            {/* Fallback overlay for 3D models */}
                            <div className="absolute inset-0 bg-purple-600/20 flex items-center justify-center">
                              <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2L3 7v6l7 5 7-5V7l-7-5zM8.5 8.5L10 7l1.5 1.5L10 10 8.5 8.5z" />
                              </svg>
                            </div>
                            <div className="absolute inset-0 bg-purple-600/20 flex items-center justify-center">
                              <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2L3 7v6l7 5 7-5V7l-7-5zM8.5 8.5L10 7l1.5 1.5L10 10 8.5 8.5z" />
                              </svg>
                            </div>
                          </div>
                        ) : (
                          <AuthenticatedImage
                            src={file.thumbnail_url || file.file_url}
                            alt={file.original_filename}
                            className="w-full h-full object-cover"
                            clientId={currentClient?.id}
                          />
                        )}

                        {file.mime_type.startsWith('video') && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-6 h-6 bg-black/50 rounded-full flex items-center justify-center">
                              <Play className="w-3 h-3 text-white ml-0.5" />
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-base sm:text-lg font-bold text-slate-900 dark:text-white truncate">
                          {file.original_filename}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-400">
                          {mediaService.formatFileSize(file.file_size)} • {new Date(file.created_at).toLocaleDateString()}
                        </p>
                      </div>

                      {/* Hover Actions */}
                      {!selectionMode && (
                        <div className={`absolute right-4 transition-opacity duration-300 flex items-center space-x-2 z-10 ${selectedFiles.has(file.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                          <input
                            type="checkbox"
                            checked={selectedFiles.has(file.id)}
                            onChange={() => handleFileSelect(file.id)}
                            className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                            onClick={(e) => e.stopPropagation()}
                            title="Select file"
                          />
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb')) {
                                setSelected3DModel(file);
                                setShow3DViewer(true);
                              } else {
                                setSelectedMedia(file);
                              }
                            }}
                            className="bg-white/20 hover:bg-white/30 text-slate-700 dark:text-white border-none backdrop-blur-sm"
                            title="Preview"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          {/* AR Creation Button - Only show for 3D models */}
                          {(file.mime_type === 'model/gltf-binary' || file.original_filename.endsWith('.glb')) && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('🎯 AR Button clicked (List) for:', file.original_filename);
                                handleCreateAR(file);
                              }}
                              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-none backdrop-blur-sm shadow-lg"
                              title="Create AR"
                            >
                              <Smartphone className="h-4 w-4" />
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadImage(file);
                            }}
                            className="bg-white/20 hover:bg-white/30 text-slate-700 dark:text-white border-none backdrop-blur-sm"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {canDelete() && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSingleDelete(file);
                              }}
                              className="bg-red-500/80 hover:bg-red-600/90 text-white border-none backdrop-blur-sm"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Selection Indicator */}
                      {selectedFiles.has(file.id) && (
                        <div className="absolute inset-0 bg-blue-500/20 border-2 border-blue-500 rounded-2xl pointer-events-none"></div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Pagination Controls */}
          {filteredFiles.length > 0 && (
            <div className="flex justify-center items-center mt-8 space-x-4">
              <Button
                onClick={() => loadMediaFiles(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                variant="outline"
                className="bg-white/70 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-300/50 dark:border-slate-600/30 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </Button>

              <div className="flex items-center space-x-2 px-4 py-2 bg-white/70 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-300/50 dark:border-slate-600/30 rounded-lg">
                <span className="text-sm text-slate-600 dark:text-slate-400">Page</span>
                <span className="text-lg font-bold text-slate-900 dark:text-white">{currentPage}</span>
              </div>

              <Button
                onClick={() => loadMediaFiles(currentPage + 1)}
                disabled={!hasMore || loading}
                variant="outline"
                className="bg-white/70 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-300/50 dark:border-slate-600/30 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Next
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <ModernFileUpload
          onClose={() => setShowUpload(false)}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {/* Modern Preview Modal */}
      {selectedMedia && (
        <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
          <DialogContent className="bg-gradient-to-br from-white/98 via-slate-50/95 to-white/98 dark:from-slate-900/98 dark:via-slate-800/95 dark:to-slate-900/98 backdrop-blur-xl border border-slate-200/60 dark:border-slate-700/60 shadow-2xl max-w-7xl w-[95vw] sm:w-full h-[90vh] overflow-hidden flex flex-col mt-8">
            {/* Enhanced Header */}
            <DialogHeader className="border-b border-slate-200/50 dark:border-slate-700/50 pb-4 mb-6">
              <div className="flex items-center justify-between pr-12">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                    {selectedMedia.mime_type.startsWith('video') ? (
                      <Video className="w-6 h-6 text-white" />
                    ) : selectedMedia.mime_type === 'model/gltf-binary' ? (
                      <Box className="w-6 h-6 text-white" />
                    ) : (
                      <Image className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <DialogTitle className="text-xl font-bold text-slate-900 dark:text-slate-100 truncate max-w-md">
                      {selectedMedia.original_filename}
                    </DialogTitle>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {mediaService.formatFileSize(selectedMedia.file_size)} • {selectedMedia.mime_type} • {mediaFiles.findIndex(f => f.id === selectedMedia.id) + 1} of {mediaFiles.length}
                    </p>
                  </div>
                </div>

                {/* Navigation and Zoom Controls */}
                <div className="flex items-center space-x-2">
                  {/* Navigation buttons */}
                  <Button
                    onClick={() => {
                      const currentIndex = mediaFiles.findIndex(f => f.id === selectedMedia.id);
                      const prevIndex = currentIndex > 0 ? currentIndex - 1 : mediaFiles.length - 1;
                      setSelectedMedia(mediaFiles[prevIndex]);
                    }}
                    variant="outline"
                    size="sm"
                    className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-slate-300/50 dark:border-slate-600/50 hover:bg-slate-100/80 dark:hover:bg-slate-700/80"
                  >
                    ←
                  </Button>
                  <Button
                    onClick={() => {
                      const currentIndex = mediaFiles.findIndex(f => f.id === selectedMedia.id);
                      const nextIndex = currentIndex < mediaFiles.length - 1 ? currentIndex + 1 : 0;
                      setSelectedMedia(mediaFiles[nextIndex]);
                    }}
                    variant="outline"
                    size="sm"
                    className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-slate-300/50 dark:border-slate-600/50 hover:bg-slate-100/80 dark:hover:bg-slate-700/80"
                  >
                    →
                  </Button>
                  
                  {/* Zoom controls */}
                  <div className="flex items-center space-x-1 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-300/50 dark:border-slate-600/50 rounded-lg px-2 py-1">
                    <Button
                      onClick={handleZoomOut}
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-slate-100/80 dark:hover:bg-slate-700/80"
                    >
                      -
                    </Button>
                    <span className="text-xs font-medium min-w-[3rem] text-center">
                      {Math.round(imageZoom * 100)}%
                    </span>
                    <Button
                      onClick={handleZoomIn}
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-slate-100/80 dark:hover:bg-slate-700/80"
                    >
                      +
                    </Button>
                    <Button
                      onClick={handleZoomReset}
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs hover:bg-slate-100/80 dark:hover:bg-slate-700/80"
                    >
                      Reset
                    </Button>
                  </div>

                  <Button
                    onClick={() => downloadImage(selectedMedia)}
                    variant="outline"
                    size="sm"
                    className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-slate-300/50 dark:border-slate-600/50 hover:bg-slate-100/80 dark:hover:bg-slate-700/80"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  {canDelete() && (
                    <Button
                      onClick={() => handleSingleDelete(selectedMedia)}
                      variant="outline"
                      size="sm"
                      className="bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm border-red-300/50 dark:border-red-700/50 text-red-600 dark:text-red-400 hover:bg-red-100/80 dark:hover:bg-red-900/30"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </DialogHeader>

            {/* Main Content - Much Larger Image with Smaller Sidebar */}
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 flex-1 min-h-0">
              {/* Large Image Section - Takes up 4/5 of the space */}
              <div className="xl:col-span-4 relative min-h-0">
                <div 
                  ref={imageContainerRef}
                  className="h-full bg-gradient-to-br from-slate-100/50 via-white/80 to-slate-100/50 dark:from-slate-800/50 dark:via-slate-900/80 dark:to-slate-800/50 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-inner overflow-hidden flex items-center justify-center"
                  style={{ cursor: imageZoom > 0.60 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  {currentClient ? (
                    selectedMedia.mime_type.startsWith('video') ? (
                      <video
                        src={selectedMedia.file_url}
                        className="w-full h-full object-contain rounded-2xl"
                        controls
                        preload="metadata"
                      />
                    ) : (
                      <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                        <div
                          style={{
                            transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${imageZoom})`,
                            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                            transformOrigin: 'center center'
                          }}
                          className="flex items-center justify-center"
                        >
                          <AuthenticatedImage
                            src={selectedMedia.file_url}
                            alt={selectedMedia.original_filename}
                            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
                            clientId={currentClient.id}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              width: 'auto',
                              height: 'auto'
                            }}
                          />
                        </div>
                        
                        {/* Navigation overlay buttons - only show when at initial zoom */}
                        {imageZoom <= 0.7 && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const currentIndex = mediaFiles.findIndex(f => f.id === selectedMedia.id);
                                const prevIndex = currentIndex > 0 ? currentIndex - 1 : mediaFiles.length - 1;
                                setSelectedMedia(mediaFiles[prevIndex]);
                              }}
                              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm z-10"
                            >
                              ←
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const currentIndex = mediaFiles.findIndex(f => f.id === selectedMedia.id);
                                const nextIndex = currentIndex < mediaFiles.length - 1 ? currentIndex + 1 : 0;
                                setSelectedMedia(mediaFiles[nextIndex]);
                              }}
                              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm z-10"
                            >
                              →
                            </button>
                          </>
                        )}

                        {/* Zoom hint */}
                        {imageZoom <= 0.7 && (
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm opacity-60">
                            Ctrl/Cmd + scroll to zoom
                          </div>
                        )}

                        {/* Pan hint when zoomed */}
                        {imageZoom > 0.7 && (
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm opacity-60">
                            Drag to pan • Scroll to zoom
                          </div>
                        )}
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Image className="w-8 h-8 text-slate-400" />
                        </div>
                        <p className="text-slate-500 dark:text-slate-400">Client not selected</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Compact Sidebar - Takes up 1/5 of the space */}
              <div className="xl:col-span-1 space-y-4 overflow-y-auto max-h-full">
                {/* File Details Card */}
                <div className="bg-gradient-to-br from-white/90 via-slate-50/80 to-white/90 dark:from-slate-800/90 dark:via-slate-700/80 dark:to-slate-800/90 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-4 shadow-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <FileText className="w-3 h-3 text-white" />
                    </div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">Details</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-slate-200/50 dark:border-slate-600/50">
                      <span className="text-sm text-slate-600 dark:text-slate-400">Size</span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {mediaService.formatFileSize(selectedMedia.file_size)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-200/50 dark:border-slate-600/50">
                      <span className="text-sm text-slate-600 dark:text-slate-400">Type</span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {selectedMedia.mime_type.split('/')[1]?.toUpperCase() || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-200/50 dark:border-slate-600/50">
                      <span className="text-sm text-slate-600 dark:text-slate-400">Created</span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {new Date(selectedMedia.created_at).toLocaleDateString()}
                      </span>
                    </div>

                  </div>
                </div>

                {/* AR Experience Card */}
                {selectedMedia.ar_viewer_url && (
                  <div className="bg-gradient-to-br from-emerald-50/90 via-green-50/80 to-emerald-50/90 dark:from-emerald-900/30 dark:via-green-900/20 dark:to-emerald-900/30 backdrop-blur-sm rounded-2xl border border-emerald-200/60 dark:border-emerald-700/60 p-4 shadow-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center">
                        <Smartphone className="w-3 h-3 text-white" />
                      </div>
                      <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 text-sm">AR Experience</h3>
                    </div>

                    <div className="space-y-3">
                      {/* AR URL with Copy Button */}
                      <div>
                        <label className="text-xs text-emerald-700 dark:text-emerald-300 font-medium mb-1 block">
                          AR Viewer URL
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={selectedMedia.ar_viewer_url}
                            readOnly
                            className="w-full bg-white/90 dark:bg-slate-700/90 text-slate-700 dark:text-slate-300 text-xs px-3 py-2 pr-16 rounded-lg border border-emerald-300/50 dark:border-emerald-600/50 focus:outline-none backdrop-blur-sm font-mono"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              navigator.clipboard.writeText(selectedMedia.ar_viewer_url!);
                              toast({
                                title: "URL Copied!",
                                description: "AR viewer URL copied to clipboard",
                              });
                            }}
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-12 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100/80 dark:hover:bg-emerald-800/30 text-xs"
                          >
                            Copy
                          </Button>
                        </div>
                      </div>

                      {/* QR Code Toggle */}
                      <div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowQRCode(!showQRCode)}
                          className="w-full bg-white/80 dark:bg-slate-700/80 border-emerald-300/50 dark:border-emerald-600/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100/80 dark:hover:bg-emerald-800/30 text-xs"
                        >
                          <Smartphone className="h-3 w-3 mr-1" />
                          {showQRCode ? 'Hide QR Code' : 'Show QR Code'}
                        </Button>
                      </div>

                      {/* QR Code Section */}
                      <AnimatePresence>
                        {showQRCode && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95, height: 0 }}
                            animate={{ opacity: 1, scale: 1, height: 'auto' }}
                            exit={{ opacity: 0, scale: 0.95, height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="bg-white/95 dark:bg-slate-800/95 rounded-xl border border-emerald-300/50 dark:border-emerald-600/50 p-1 backdrop-blur-sm">
                              <div className="flex flex-col items-center space-y-1">
                                <div id="qr-code" className="bg-white rounded">
                                  <QRCodeGenerator url={selectedMedia.ar_viewer_url} size={100} />
                                </div>
                                <div className="flex gap-1 w-full">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => downloadQRCode()}
                                    className="flex-1 bg-emerald-50/80 dark:bg-emerald-900/30 border-emerald-300/50 dark:border-emerald-600/50 text-emerald-700 dark:text-emerald-300 text-xs"
                                  >
                                    <Download className="h-3 w-3 mr-1" />
                                    Download
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      // Share functionality if available
                                      if (navigator.share) {
                                        navigator.share({
                                          title: 'AR Experience',
                                          text: 'Check out this AR experience!',
                                          url: selectedMedia.ar_viewer_url
                                        });
                                      } else {
                                        navigator.clipboard.writeText(selectedMedia.ar_viewer_url!);
                                        toast({
                                          title: "URL Copied!",
                                          description: "Share this URL to let others view the AR experience",
                                        });
                                      }
                                    }}
                                    className="flex-1 bg-emerald-50/80 dark:bg-emerald-900/30 border-emerald-300/50 dark:border-emerald-600/50 text-emerald-700 dark:text-emerald-300 text-xs"
                                  >
                                    <Share2 className="h-3 w-3 mr-1" />
                                    Share
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {/* 3D Model Actions */}
                {(selectedMedia.mime_type === 'model/gltf-binary' || selectedMedia.original_filename.endsWith('.glb')) && (
                  <div className="bg-gradient-to-br from-emerald-50/90 via-green-50/80 to-emerald-50/90 dark:from-emerald-900/30 dark:via-green-900/20 dark:to-emerald-900/30 backdrop-blur-sm rounded-2xl border border-emerald-200/60 dark:border-emerald-700/60 p-4 shadow-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center">
                        <Smartphone className="w-3 h-3 text-white" />
                      </div>
                      <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 text-sm">3D Actions</h3>
                    </div>
                    <Button
                      onClick={() => handleCreateAR(selectedMedia)}
                      className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300"
                    >
                      <Smartphone className="h-4 w-4 mr-2" />
                      Create AR Ad
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Continue with existing modals... */}


{/* Delete Confirmation Modal */ }
<MediaDeleteConfirmDialog
  isOpen={showDeleteConfirm}
  onClose={() => setShowDeleteConfirm(false)}
  onConfirm={handleDeleteFiles}
  files={filesToDelete}
  isDeleting={isDeleting}
/>

{/* 3D Model Viewer Modal */ }
{
  show3DViewer && selected3DModel && (
    <Dialog open={show3DViewer} onOpenChange={() => setShow3DViewer(false)}>
      <DialogContent className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-200/50 dark:border-slate-700/50 max-w-6xl w-[95vw] sm:w-full max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-slate-100 truncate pr-12">
            3D Model: {selected3DModel.original_filename}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <ThreeDViewer
            modelUrl={selected3DModel.file_url}
            mediaId={selected3DModel.id}
            className="w-full h-[60vh]"
            showControls={true}
          />

          <div className="flex justify-between items-center text-sm text-slate-600 dark:text-slate-400">
            <div>
              <p><strong>Size:</strong> {mediaService.formatFileSize(selected3DModel.file_size)}</p>
              <p><strong>Type:</strong> {selected3DModel.mime_type}</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => {
                  handleCreateAR(selected3DModel);
                  setShow3DViewer(false);
                }}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
              >
                <Smartphone className="h-4 w-4 mr-2" />
                Create AR
              </Button>
              <Button
                onClick={() => setShow3DViewer(false)}
                variant="outline"
                className="text-slate-600 dark:text-slate-400"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
    </div >
  );
};

export default MediaLibrary;